/** biome-ignore-all lint/complexity/noUselessFragments: false positive */
import { isWebDataFetching } from '@cobliteam/api-lib';
import { AuthToken } from '@cobliteam/api-lib/src/account/session';
import { Location } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { lazy } from '@cobliteam/lazy-with-retries';
import AppAnnouncement from '@cobliteam/ui-business/components/AppAnnouncement/AppAnnouncement';
import { memo } from 'react';
import { createPortal } from 'react-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import useRedirectAfterLogin from './hooks/useRedirectAfterLogin';
import AppLayout from './layouts/AppLayout';
import AuthLayout from './layouts/AuthLayout';
import NoGroupsLayout from './layouts/NoGroupsLayout';
import HubSpotSSORedirect, { saveUrlToRedirectHubSpot } from './libs/HubSpotSSORedirect';
import ErrorPage from './pages/ErrorPage';
import LoadingPage from './pages/LoadingPage';
import DashboardProvider, { useDashboard } from './providers/DashboardProvider';
import FeatureFlagsProvider from './providers/FeatureFlagsProvider';
import PrivateRouterProvider from './providers/PrivateRouterProvider';
import PublicRouterProvider from './providers/PublicRouterProvider';
import routes, { type IRoute } from './routes';
import type { AppState } from './store';

const Login = lazy(() => import('@cobliteam/login/src/pages/Login'));
const PasswordRecovery = lazy(() => import('@cobliteam/login/src/pages/PasswordRecovery'));
const NoFleetAccess = lazy(() => import('@cobliteam/login/src/pages/NoFleetAccess'));
const TokenValidation = lazy(() => import('@cobliteam/login/src/pages/TokenValidation'));
const PasswordReset = lazy(() => import('@cobliteam/login/src/pages/PasswordReset'));

const LayoutSwitcher = ({ children }: { children: React.ReactNode }) => {
  const { runtimeFlags } = useDashboard();
  if (runtimeFlags.noValidGroupOrVehicles) return <NoGroupsLayout />;
  const layout = <AppLayout>{children}</AppLayout>;

  return layout;
};

// 🔐 Auth Routes
const Auth = () => (
  <Route exact path={[Location.LOGIN, Location.PASSWORD_RECOVERY, Location.TOKEN_VALIDATION, Location.PASSWORD_RESET]}>
    <AuthLayout>
      <Route exact path={Location.LOGIN} component={Login} />
      <Route exact path={Location.PASSWORD_RECOVERY} component={PasswordRecovery} />
      <Route exact path={Location.TOKEN_VALIDATION} component={TokenValidation} />
      <Route exact path={Location.PASSWORD_RESET} component={PasswordReset} />
    </AuthLayout>
  </Route>
);

// 🌐 Public Routes
const Public = () => (
  <PublicRouterProvider>
    <Route exact path={Location.NO_FLEET_ACCESS} component={NoFleetAccess} />
    <Auth />
  </PublicRouterProvider>
);

// 🔒 Private Routes
const Private = () => {
  return (
    <PrivateRouterProvider>
      <HubSpotSSORedirect>
        <FeatureFlagsProvider>
          <DashboardProvider>
            <LayoutSwitcher>
              {routes.map((route) => (
                <ErrorBoundary
                  key={route.path}
                  FallbackComponent={({ error, resetErrorBoundary }) => <ErrorPage path={route.path} error={error} resetErrorBoundary={resetErrorBoundary} />}
                  onError={(error, errorInfo) => {
                    DatadogTrack.error(error, {
                      component: 'Router',
                      route: route.path,
                      errorInfo,
                    });
                  }}
                >
                  <PrivateRoute key={route.path} route={route as IRoute} />
                </ErrorBoundary>
              ))}
            </LayoutSwitcher>
            {createPortal(<AppAnnouncement />, document.body)}
          </DashboardProvider>
        </FeatureFlagsProvider>
      </HubSpotSSORedirect>
    </PrivateRouterProvider>
  );
};

//  🔂 Redirect /# -> /
const RedirectHash = memo(() => {
  const authToken = localStorage.getItem(AuthToken.ACCESS_TOKEN);
  return (
    <Route
      path='/'
      render={({ location }) => {
        if (!location.hash.includes('#')) return null;
        if (!authToken) return <Redirect to={Location.LOGIN} />;
        return <Redirect to={location.hash.replace('#', '')} />;
      }}
    />
  );
});

const Router = memo(() => {
  const loginWebData = useSelector((state: AppState) => state.login.loginWebData);
  useRedirectAfterLogin();
  saveUrlToRedirectHubSpot();

  if (isWebDataFetching(loginWebData)) return <LoadingPage showLogo message='Carregando dados de acesso...' />;

  return (
    <Switch>
      <>
        {/* 🌐 Public Routes */}
        <Public />
        {/* 🔒 Private Routes */}
        <Private />
        {/* 🔂 Redirect /# -> / */}
        <RedirectHash />
      </>
    </Switch>
  );
});

export default Router;
